import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import {
  getStoredImpersonationSessionContext,
  getStoredImpersonationSessionId,
} from "@/lib/impersonationContext";
import { createTenantActivityHeartbeat } from "@/lib/tenantActivityHeartbeat";

const AuthContext = createContext(null);

function clinicIdOf(clinic) {
  return clinic?.clinic_id || clinic?.id || null;
}

function impersonationUser(session, sessionId) {
  const targetUserId = session?.target_user_id || null;
  if (!targetUserId || !sessionId) return null;
  const name = session?.target_user_name || null;
  return {
    id: targetUserId,
    email: session?.target_user_email || null,
    user_metadata: {
      full_name: name,
      display_name: name,
    },
    app_metadata: {
      impersonation_session_id: sessionId,
      impersonated_by_super_admin: true,
    },
  };
}

export function deriveAuthClinicContext({
  claims = null,
  clinics = [],
  impersonationSessionId = null,
  impersonationSession = null,
  sessionUser = null,
} = {}) {
  const clinicList = Array.isArray(clinics) ? clinics : [];
  const activeClinicFromList = clinicList.find((c) => c?.is_active_clinic) ?? null;
  const claimedClinicId = claims?.active_clinic_id ?? null;
  const isImpersonating = Boolean(impersonationSessionId);
  const activeClinic =
    activeClinicFromList
    ?? clinicList.find((c) => clinicIdOf(c) === claimedClinicId)
    ?? null;

  return {
    activeClinicFromList,
    activeClinicId: clinicIdOf(activeClinic) ?? claimedClinicId ?? null,
    activeClinic,
    clinicRole: activeClinic?.role ?? claims?.clinic_role ?? null,
    user: isImpersonating ? impersonationUser(impersonationSession, impersonationSessionId) : sessionUser,
    isPlatformAdmin: Boolean(claims?.is_platform_admin) && !isImpersonating,
  };
}

export function AuthProvider({ children }) {
  const [session, setSession]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [clinics, setClinics]       = useState([]);     // [{clinic_id, clinic_name, role, is_active_clinic, ...}]
  const [clinicsLoading, setClinicsLoading] = useState(true);
  const [clinicsFetched, setClinicsFetched] = useState(false);
  const [mfaLevel, setMfaLevel] = useState("aal1");

  // ---- session bootstrap + subscription -----------------------------
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session ?? null);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, s) => {
      setSession(s ?? null);
      setLoading(false);
    });
    return () => {
      cancelled = true;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  const [clinicsError, setClinicsError] = useState(null);

  const refreshMfaAssurance = useCallback(async () => {
    if (!supabase.auth.mfa?.getAuthenticatorAssuranceLevel) {
      setMfaLevel("aal1");
      return "aal1";
    }
    const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    const level = data?.currentLevel || data?.current_level || "aal1";
    setMfaLevel(level);
    return level;
  }, []);

  // ---- clinics fetcher ----------------------------------------------
  const refreshClinics = useCallback(async () => {
    setClinicsLoading(true);
    setClinicsError(null);
    const { data: current } = await supabase.auth.getSession();
    if (!current.session && !getStoredImpersonationSessionId()) {
      // No session yet — leave clinicsFetched FALSE so the ProtectedRoute
      // loading gate keeps spinning until the real session resolves and
      // a real list_my_clinics call completes. Setting it true here would
      // create a one-frame stale window where a freshly-authed user
      // looks like "fetched with zero clinics" and gets bounced to
      // /signup/clinic.
      setClinics([]);
      setClinicsLoading(false);
      return [];
    }
    const { data, error } = await supabase.rpc("list_my_clinics");
    if (!error) {
      setClinicsLoading(false);
      setClinicsFetched(true);
      setClinics(data ?? []);
      return data ?? [];
    }
    // eslint-disable-next-line no-console
    console.warn("[auth] list_my_clinics failed", error);
    setClinicsLoading(false);
    setClinicsFetched(true);
    setClinicsError(error);
    // Do NOT wipe clinics on error — preserve any previously-loaded list
    // so ProtectedRoute doesn't bounce the user to /signup/clinic on a
    // transient bootstrap hiccup.
    return null;
  }, []);

  useEffect(() => {
    // Reset the "fetched" flag whenever the identity changes so
    // ProtectedRoute's loading gate waits for the new fetch.
    setClinicsFetched(false);
    refreshMfaAssurance();
    refreshClinics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  useEffect(() => {
    const onImpersonationChanged = () => {
      setClinicsFetched(false);
      refreshClinics();
    };
    window.addEventListener("dental-os:impersonation-session-started", onImpersonationChanged);
    window.addEventListener("dental-os:impersonation-session-ended", onImpersonationChanged);
    window.addEventListener("storage", onImpersonationChanged);
    return () => {
      window.removeEventListener("dental-os:impersonation-session-started", onImpersonationChanged);
      window.removeEventListener("dental-os:impersonation-session-ended", onImpersonationChanged);
      window.removeEventListener("storage", onImpersonationChanged);
    };
  }, [refreshClinics]);

  // ---- derived helpers ---------------------------------------------
  const claims          = session?.access_token ? decodeClaims(session.access_token) : null;
  const impersonationSessionId = getStoredImpersonationSessionId();
  const impersonationSession = getStoredImpersonationSessionContext();
  const {
    activeClinicId,
    activeClinic,
    clinicRole,
    user,
    isPlatformAdmin,
  } = deriveAuthClinicContext({
    claims,
    clinics,
    impersonationSessionId,
    impersonationSession,
    sessionUser: session?.user ?? null,
  });

  useEffect(() => {
    const heartbeat = createTenantActivityHeartbeat({ supabase });
    heartbeat.start({
      userId: user?.id,
      activeClinicId,
    });
    return () => heartbeat.stop();
  }, [user?.id, activeClinicId]);

  // ---- actions ------------------------------------------------------
  const signIn = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  }, []);

  const signUp = useCallback(async (email, password, options = {}) => {
    const signUpOptions = {};
    if (options.emailRedirectTo) signUpOptions.emailRedirectTo = options.emailRedirectTo;
    if (options.data) signUpOptions.data = options.data;
    const payload = Object.keys(signUpOptions).length
      ? { email, password, options: signUpOptions }
      : { email, password };
    const { data, error } = await supabase.auth.signUp(payload);
    return { data, error };
  }, []);

  const resetPasswordForEmail = useCallback(async (email) => {
    const redirectTo = `${window.location.origin}/reset-password`;
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    return { data, error };
  }, []);

  const updatePassword = useCallback(async (password) => {
    const { data, error } = await supabase.auth.updateUser({ password });
    return { data, error };
  }, []);

  const signOut = useCallback(async (options) => {
    await supabase.auth.signOut(options);
    setClinics([]);
  }, []);

  const createClinicForOwner = useCallback(async (payload) => {
    // payload = {name, country, currency, timezone, p_seed_sample_patient}
    const { data, error } = await supabase.rpc("create_clinic_for_owner", {
      p_payload: payload,
    });
    if (error) return { data: null, error };
    // Refresh JWT so the hook injects active_clinic_id baked-in by the RPC.
    await supabase.auth.refreshSession();
    await refreshClinics();
    return { data, error: null };
  }, [refreshClinics]);

  const switchClinic = useCallback(async (clinicId) => {
    const { error } = await supabase.rpc("set_active_clinic", { p_clinic_id: clinicId });
    if (error) return { error };
    await supabase.auth.refreshSession();
    await refreshClinics();
    return { error: null };
  }, [refreshClinics]);

  const value = useMemo(() => ({
    session,
    user,
    loading,
    clinics,
    clinicsLoading,
    clinicsFetched,
    clinicsError,
    activeClinicId,
    activeClinic,
    clinicRole,
    mfaLevel,
    isPlatformAdmin,
    refreshClinics,
    refreshMfaAssurance,
    signIn, signUp, resetPasswordForEmail, updatePassword, signOut,
    createClinicForOwner,
    switchClinic,
  }), [session, user, loading, clinics, clinicsLoading, clinicsFetched, clinicsError, activeClinicId, activeClinic, clinicRole, isPlatformAdmin, mfaLevel,
       refreshClinics, refreshMfaAssurance, signIn, signUp, resetPasswordForEmail, updatePassword, signOut, createClinicForOwner, switchClinic]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

// ---- helpers -------------------------------------------------------
function decodeClaims(jwt) {
  try {
    const payload = jwt.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return null;
  }
}
